/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

/*
 * HeroContainer.jsx
 * Created by Destin frasier on 05/10/21
 * Updated on 09/21/21
 */

import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import throttle from 'lodash/throttle';
import { connect } from 'react-redux';

import {
    SearchActions,
    Icon,
    FlexRow,
    FlexCol,
    Tabs,
    TabPanel,
    DropdownMenu,
    Button,
    Radio,
    RadioGroup,
    Divider,
    Badge,
    withLDConsumer,
} from 'sarsaparilla';
import * as MapHelper from 'shared-ui/maps/helpers/mapHelper';
import { SearchAutosuggest } from 'shared-ui/search-autosuggest';

import { HOMEPAGE_HERO_INVENTORY } from 'site-kit';

import { searchDate } from 'ui-search/src/utils/dateUtils';

import { CAMPSITE_ACCESSIBLE_FILTER as accessibilityFilter } from 'ui-search/src/constants';
import * as searchActions from 'ui-search/src/actions/search';

import { PhotoInfoFlyout } from '../../components/homepage/hero/PhotoInfoFlyout';
import { HeroImage, getRandomHeroImage } from '../../components/homepage/hero/HeroImage';
import HeroAiSearch from '../../components/homepage/hero/HeroAiSearch';
import { SEARCH } from '../../constants/propTypes.js';
import AccessibleCheckbox from '../../components/sharedComponents/AccessibleCheckbox';
import { ExploreAllQuickPromos } from './ExploreAllQuickPromos/ExploreAllQuickPromos';
import MobileSearch from './MobileSearch';
import TourTimeFilter from './TourTimeFilter';
import { HeroDates } from './HeroDates';
import { HeroSingleDate } from './HeroSingleDate';
import { CampingCarousel } from '../../components/homepage/recommendationCarousels/heroCarousels/CampingCarousel';
import { TicketsCarousel } from '../../components/homepage/recommendationCarousels/heroCarousels/TicketsCarousel';
import { PermitsCarousel as PermitsCarouselNew } from '../../components/homepage/recommendationCarousels/heroCarousels/PermitsCarousel';
import { VenuesCarousel } from '../../components/homepage/recommendationCarousels/heroCarousels/VenuesCarousel';

const SEARCH_CAL_DATE_FORMAT = 'MM/DD/YYYY';

const SEARCH_INVENTORY_TYPES = {
    CAMPING_TAB: ['campground', 'recarea'],
    TICKETS_TOURS_TAB: ['ticketfacility', 'timedentry'],
    PERMITS_TAB: ['permit'],
    DAY_USE_VENUES_TAB: ['dayuse', 'venuereservations'],
};
const AI_SEARCH_TAB_INDEX = 5;
const ENABLED_SEARCH_ON_SELECT_TYPES = ['kb', 'cms', 'article'];

export class HeroContainer extends React.Component {
    static propTypes = {
        search: SEARCH,
        dispatch: PropTypes.func.isRequired,
        submitSimpleSearch: PropTypes.func,
        flags: PropTypes.shape({
            useIaSearchParse: PropTypes.bool,
            homepageSearchIcons: PropTypes.bool,
        }),
    };

    constructor(props) {
        super(props);
        let startDate = null;
        const flexDates = {};

        if (props.search.checkin_time) {
            startDate = searchDate(props.search.checkin_time);
            flexDates.startDate = searchDate(props.search.checkin_time);
        }
        let endDate = null;
        if (props.search.checkout_time) {
            endDate = searchDate(props.search.checkout_time);
            flexDates.endDate = searchDate(props.search.checkout_time);
        }
        if (props.search.avail_nights) {
            flexDates.duration = props.search.avail_nights;
        }
        if (props.search.flex_start_range) {
            flexDates.startRange = props.search.flex_start_range;
        }
        if (props.search.flex_end_range) {
            flexDates.endRange = props.search.flex_end_range;
        }
        if (props?.search?.avail_months) {
            flexDates.months = props?.search?.avail_months?.map((item) =>
                searchDate(item).utc().format('MM-YYYY')
            );
        }
        const activeSearchTabIndex = Number(
            window.sessionStorage.getItem('home-active-search-tab-index') || 0
        );

        const waysToStay = 'All';

        this.state = {
            photoFlyout: false,
            useMobile: false,
            searchPlaceholder: this.props.flags.homepageSearchIcons
                ? 'Where to?'
                : 'Search for campsites, tours, and other recreation activities ...',
            searchPlaceholderCamping: 'Location or campground name ...',
            startDate,
            endDate,
            activeSearchTabIndex,
            waysToStay,
            searchLoading: false,
            flexDatesFilter: flexDates,
            geoIp: null,
            tourTimeFilter: {
                earlyMorning: false,
                morning: false,
                earlyAfternoon: false,
                afternoon: false,
                evening: false,
                lateNight: false,
            },
        };

        this.measurePage = throttle(this.measurePage.bind(this), 50);
        this.randomHeroImage = getRandomHeroImage();
    }

    componentDidMount() {
        const { dispatch } = this.props;

        dispatch(
            SearchActions.updateSearchCriterias({
                what: '',
                headerTextQuery: '',
            })
        );

        SearchActions.fetchUserGeoIp()
            .then((geoIp) => {
                this.setState({
                    geoIp,
                });
            })
            .catch(() => {
                // This code commented out because it is not valid
                // this.setState({
                //     [stateKey]: {
                //         ...stateObjectByKey,
                //         hasErrors: true,
                //     },
                // });
            });

        window.addEventListener('pageshow', this.pageShowListener);
        window.addEventListener('resize', this.measurePage);
        this.measurePage();
        window.addEventListener('keydown', this.handleKeyDown);
        dispatch(
            SearchActions.updateSearchCriterias({
                selectedSuggestion: null,
            })
        );
    }

    // eslint-disable-next-line
    componentDidUpdate(prevProps, prevState) {
        if (this.state.geoIp !== prevState.geoIp) {
            this.handleGetAvailableThisWeekend();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.measurePage);
        window.removeEventListener('keydown', this.handleKeyDown);
        window.removeEventListener('scroll', this.closePhotoCredit);
        window.removeEventListener('pageshow', this.pageShowListener);
    }

    pageShowListener = (event) => {
        if (event.persisted) {
            this.setState({ searchLoading: false });
        }
    };

    onSingleDateChange = (date) => {
        this.setState({
            startDate: date,
            endDate: date,
        });
    };

    onTourTimeChange = (tourTimeFilter) => {
        this.setState({ tourTimeFilter });
    };

    openPhotoCredit = () => {
        document.addEventListener('scroll', this.closePhotoCredit);

        this.setState({
            photoFlyout: true,
        });
    };

    closePhotoCredit = () => {
        document.removeEventListener('scroll', this.closePhotoCredit);

        this.setState({
            photoFlyout: false,
        });
    };

    measurePage = () => {
        const { windowWidth: stateWindowWidth } = this.state;
        const windowWidth = window.innerWidth;
        const isMobile = windowWidth < 768;

        /**
         * As we are displaying a different search on mobile, we ensure to
         * restore desktop container to its original state
         */
        if (isMobile) {
            this.closePhotoCredit();
        }

        if (stateWindowWidth !== windowWidth) {
            this.setState({
                windowWidth,
            });
        }

        this.setState({ useMobile: isMobile });
    };

    submitSearch = (e) => {
        try {
            e.preventDefault();

            const { search } = this.props;

            this.setState({ searchLoading: true });

            this.runSearch({
                searchText: search.what,
                suggestion: search.selectedSuggestion,
            });
        } catch (err) {
            this.setState({ searchLoading: false });
        }
        return false;
    };

    parseFlexDatesParams = (months, duration) => {
        let monthsArray = [];
        if (months) {
            months.map((item) => {
                const av = searchDate(item)
                    .startOf('months')
                    .format('YYYY-MM-DD[T]HH:mm:ss[Z]');
                monthsArray = [...monthsArray, av];
                return item;
            });
        }
        return { avail_months: monthsArray, avail_nights: duration };
    };

    applyCampingUIFilters = () => {
        const { search, dispatch } = this.props;
        const { startDate, endDate, flexDatesFilter, waysToStay } = this.state;
        const campsiteAmenities = search?.uiFilters?.campsiteAmenities;
        const isAccessibleChecked =
            campsiteAmenities != null &&
            Object.hasOwn(campsiteAmenities, accessibilityFilter.key);

        dispatch(SearchActions.resetSearchFilters());

        let startDateStr = null;
        if (startDate) {
            startDateStr = searchDate(startDate)
                .startOf('day')
                .format(SEARCH_CAL_DATE_FORMAT);
        }
        let endDateStr = null;
        if (endDate) {
            endDateStr = searchDate(endDate)
                .startOf('day')
                .format(SEARCH_CAL_DATE_FORMAT);
        }

        if (flexDatesFilter?.startDate && flexDatesFilter.endDate) {
            startDateStr = searchDate(flexDatesFilter?.startDate)
                .startOf('day')
                .format(SEARCH_CAL_DATE_FORMAT);
            endDateStr = searchDate(flexDatesFilter?.endDate)
                .startOf('day')
                .format(SEARCH_CAL_DATE_FORMAT);
        }
        let startRange = null;
        if (flexDatesFilter.startRange) {
            startRange = flexDatesFilter.startRange;
        }

        let endRange = null;
        if (flexDatesFilter.endRange) {
            endRange = flexDatesFilter.endRange;
        }
        const { avail_months, avail_nights } = this.parseFlexDatesParams(
            flexDatesFilter?.months,
            flexDatesFilter?.duration
        );

        if (avail_months && avail_nights) {
            startDateStr = '';
            endDateStr = '';
        }
        let avMonths = avail_months;
        let avNights = avail_nights;
        if (!flexDatesFilter || Object.keys(flexDatesFilter).length === 0) {
            startDateStr = '';
            endDateStr = '';
            avMonths = [];
            avNights = '';
            endRange = '';
            startRange = '';
        }
        dispatch(
            SearchActions.updateSearchCriterias({
                checkin_time: startDateStr,
                checkout_time: endDateStr,
                flex_start_range: startRange,
                flex_end_range: endRange,
                avail_nights: avNights || '',
                avail_months: avMonths || [],
                sort: 'available',
            })
        );

        const uiFilters = {
            inventoryTypes: HOMEPAGE_HERO_INVENTORY.CAMPING_TAB,
        };

        const fgParams = search?.fg ?? [];
        if (waysToStay === 'Tent Camping') {
            dispatch(SearchActions.updateSearchCriteria('fg', [...fgParams, 'tent']));
        }

        if (waysToStay === 'RV/Motorhome/Trailer Camping') {
            dispatch(SearchActions.updateSearchCriteria('fg', [...fgParams, 'rmt']));
        }

        if (waysToStay === 'Lodging (Cabins/Yurts/Lookouts)') {
            dispatch(SearchActions.updateSearchCriteria('fg', [...fgParams, 'lodging']));
        }

        if (isAccessibleChecked) {
            uiFilters.campsiteAmenities = {};
            uiFilters.campsiteAmenities[accessibilityFilter.key] = accessibilityFilter;
        }

        if (uiFilters) {
            Object.keys(uiFilters).forEach((key) => {
                Object.keys(uiFilters[key]).forEach((fltKey) => {
                    dispatch(SearchActions.addSearchFilter(key, uiFilters[key][fltKey]));
                });
            });
        }

        return uiFilters;
    };

    applyTicketsUIFilters = () => {
        const { dispatch } = this.props;
        const { startDate, endDate } = this.state;

        dispatch(SearchActions.resetSearchFilters());

        let startDateStr = null;
        if (startDate) {
            startDateStr = searchDate(startDate)
                .startOf('day')
                .format(SEARCH_CAL_DATE_FORMAT);
        }
        let endDateStr = null;
        if (endDate) {
            endDateStr = searchDate(endDate)
                .startOf('day')
                .format(SEARCH_CAL_DATE_FORMAT);
        }

        dispatch(
            SearchActions.updateSearchCriterias({
                checkin_time: startDateStr,
                checkout_time: endDateStr,
                sort: 'available',
            })
        );

        const uiFilters = {
            inventoryTypes: HOMEPAGE_HERO_INVENTORY.TICKETS_TOURS_TAB,
        };

        uiFilters.tourTimes = {};

        const { tourTimeFilter } = this.state;

        if (tourTimeFilter.earlyMorning) {
            uiFilters.tourTimes['tour-times-early-morning'] = {
                key: 'tour-times-early-morning',
                value: 'Early Morning (Midnight - 7:59 AM)',
                filters: [
                    {
                        attribute: 'tour_times',
                        filterValue: 'early_morning',
                    },
                ],
            };
        }

        if (tourTimeFilter.morning) {
            uiFilters.tourTimes['tour-times-morning'] = {
                key: 'tour-times-morning',
                value: 'Morning (8 - 11:59 AM)',
                filters: [
                    {
                        attribute: 'tour_times',
                        filterValue: 'morning',
                    },
                ],
            };
        }

        if (tourTimeFilter.earlyAfternoon) {
            uiFilters.tourTimes['tour-times-early-afternoon'] = {
                key: 'tour-times-early-afternoon',
                value: 'Early Afternoon (12 - 2:59 PM)',
                filters: [
                    {
                        attribute: 'tour_times',
                        filterValue: 'early_afternoon',
                    },
                ],
            };
        }

        if (tourTimeFilter.afternoon) {
            uiFilters.tourTimes['tour-times-afternoon'] = {
                key: 'tour-times-afternoon',
                value: 'Afternoon (3 - 5:59 PM)',
                filters: [
                    {
                        attribute: 'tour_times',
                        filterValue: 'afternoon',
                    },
                ],
            };
        }

        if (tourTimeFilter.evening) {
            uiFilters.tourTimes['tour-times-evening'] = {
                key: 'tour-times-evening',
                value: 'Evening (6 - 8:59 PM)',
                filters: [
                    {
                        attribute: 'tour_times',
                        filterValue: 'evening',
                    },
                ],
            };
        }

        if (tourTimeFilter.lateNight) {
            uiFilters.tourTimes['tour-times-late-night'] = {
                key: 'tour-times-late-night',
                value: 'Late Night (9 - 11:59 PM)',
                filters: [
                    {
                        attribute: 'tour_times',
                        filterValue: 'late_night',
                    },
                ],
            };
        }

        if (uiFilters) {
            Object.keys(uiFilters).forEach((key) => {
                Object.keys(uiFilters[key]).forEach((fltKey) => {
                    dispatch(SearchActions.addSearchFilter(key, uiFilters[key][fltKey]));
                });
            });
        }

        return uiFilters;
    };

    buildTourTimeFilter = () => {
        const tourTimeFilter = [];
        const ttFilter = this.state.tourTimeFilter;
        if (ttFilter) {
            if (ttFilter.earlyMorning) {
                tourTimeFilter.push('early_morning');
            }
            if (ttFilter.morning) {
                tourTimeFilter.push('morning');
            }
            if (ttFilter.earlyAfternoon) {
                tourTimeFilter.push('early_afternoon');
            }
            if (ttFilter.afternoon) {
                tourTimeFilter.push('afternoon');
            }
            if (ttFilter.evening) {
                tourTimeFilter.push('evening');
            }
            if (ttFilter.lateNight) {
                tourTimeFilter.push('late_night');
            }
        }
        return tourTimeFilter;
    };

    applyPermitsUIFilters = () => {
        const { dispatch } = this.props;

        dispatch(SearchActions.resetSearchFilters());

        dispatch(
            SearchActions.updateSearchCriterias({
                sort: 'available',
            })
        );

        const uiFilters = {
            inventoryTypes: HOMEPAGE_HERO_INVENTORY.PERMITS_TAB,
        };

        if (uiFilters) {
            Object.keys(uiFilters).forEach((key) => {
                Object.keys(uiFilters[key]).forEach((fltKey) => {
                    dispatch(SearchActions.addSearchFilter(key, uiFilters[key][fltKey]));
                });
            });
        }

        return uiFilters;
    };

    applyDayUseVenuesUiFilters = () => {
        const { dispatch } = this.props;
        const { startDate, endDate } = this.state;

        dispatch(SearchActions.resetSearchFilters());

        let startDateStr = null;
        if (startDate) {
            startDateStr = searchDate(startDate)
                .startOf('day')
                .format(SEARCH_CAL_DATE_FORMAT);
        }
        let endDateStr = null;
        if (endDate) {
            endDateStr = searchDate(endDate)
                .startOf('day')
                .format(SEARCH_CAL_DATE_FORMAT);
        }

        dispatch(
            SearchActions.updateSearchCriterias({
                checkin_time: startDateStr,
                checkout_time: endDateStr,
                sort: 'available',
            })
        );

        const uiFilters = {
            inventoryTypes: HOMEPAGE_HERO_INVENTORY.DAY_USE_VENUES_TAB,
        };

        if (uiFilters) {
            Object.keys(uiFilters).forEach((key) => {
                Object.keys(uiFilters[key]).forEach((fltKey) => {
                    dispatch(SearchActions.addSearchFilter(key, uiFilters[key][fltKey]));
                });
            });
        }

        return uiFilters;
    };

    getTabUIFilters = (tab) => {
        let filters;

        if (tab === 'Camping') {
            filters = this.applyCampingUIFilters();
        }

        if (tab === 'Tickets') {
            filters = this.applyTicketsUIFilters();
        }

        if (tab === 'Permits') {
            filters = this.applyPermitsUIFilters();
        }

        if (tab === 'Day Use / Venues') {
            filters = this.applyDayUseVenuesUiFilters();
        }

        return filters;
    };

    showMainSearchResults = (tab = '') => {
        const { dispatch, search } = this.props;
        try {
            const uiFilters = this.getTabUIFilters(tab);

            dispatch(
                searchActions.submitSearchFromNavigation(
                    search.what,
                    search.selectedSuggestion,
                    uiFilters
                )
            );
        } catch (err) {
            this.setState({ searchLoading: false });
        }
        return false;
    };

    submitCampingSearch = () => {
        const { search } = this.props;
        const { flexDatesFilter } = this.state;

        try {
            const suggestion = search.selectedSuggestion || {
                text: search.what,
            };
            const uiFilters = this.applyCampingUIFilters();

            this.setState({ searchLoading: true });

            const { avail_months, avail_nights } = this.parseFlexDatesParams(
                flexDatesFilter?.months,
                flexDatesFilter?.duration
            );

            if (avail_nights && avail_months?.length) {
                suggestion.avail_nights = avail_nights;
                suggestion.avail_months = avail_months;
            } else {
                suggestion.checkin_time = flexDatesFilter.startDate;
                suggestion.checkout_time = flexDatesFilter.endDate;
                if (flexDatesFilter.startRange) {
                    suggestion.flex_start_range = flexDatesFilter.startRange;
                }
                if (flexDatesFilter.endRange) {
                    suggestion.flex_end_range = flexDatesFilter.endRange;
                }
            }

            suggestion.uiFilters = uiFilters;
            SearchActions.addSearchHistoryItem({
                rawSearchText: search.what,
                suggestion,
            });

            this.showMainSearchResults('Camping');
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('error', err);

            this.setState({ searchLoading: false });
        }
    };

    runSearch = async ({ searchText, suggestion, searchHistory }) => {
        const { dispatch, search } = this.props;

        const campsiteAmenities = search?.uiFilters?.campsiteAmenities;
        const isAccessibleChecked =
            campsiteAmenities != null &&
            Object.hasOwn(campsiteAmenities, accessibilityFilter.key);

        const uiFilters = {};
        if (isAccessibleChecked) {
            uiFilters.campsiteAmenities = {
                ...(search?.uiFilters?.campsiteAmenities || {}),
            };
            uiFilters.inventoryTypes = { ...HOMEPAGE_HERO_INVENTORY.CAMPING_TAB };
        }

        try {
            await SearchActions.addSearchHistoryItem({
                searchHistory,
                rawSearchText: searchText,
                suggestion,
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('error saving history', e);
        }

        dispatch(
            searchActions.submitSimpleSearch(
                searchText,
                null,
                suggestion,
                null,
                uiFilters,
                false,
                null,
                null,
                null,
                false,
                search.fg ?? []
            )
        );
    };

    handleGetAvailableThisWeekend = (value) => {
        const {
            activeSearchTabIndex,
            campingTabCarouselData,
            ticketsTabCarouselData,
            permitsTabCarouselData,
            dayUseVenuesTabCarouselData,
        } = this.state;

        const currentIndex = !value ? activeSearchTabIndex : value;

        if (
            currentIndex === 1 &&
            campingTabCarouselData?.locations?.length === 0 &&
            campingTabCarouselData?.isLoading
        ) {
            this.fetchCampingNearbyLocations();
        }
        if (
            currentIndex === 2 &&
            ticketsTabCarouselData?.locations?.length === 0 &&
            ticketsTabCarouselData?.isLoading
        ) {
            this.fetchTicketsNearbyLocations();
        }
        if (
            currentIndex === 3 &&
            permitsTabCarouselData?.locations?.length === 0 &&
            permitsTabCarouselData?.isLoading
        ) {
            this.fetchPermitsNearbyLocations();
        }
        if (
            currentIndex === 4 &&
            dayUseVenuesTabCarouselData?.locations?.length === 0 &&
            dayUseVenuesTabCarouselData?.isLoading
        ) {
            this.fetchDayUseVenuesNearbyLocations();
        }
    };

    handleSearchTabChange = (newIndex) => {
        this.handleGetAvailableThisWeekend(Number(`${newIndex}`));
        this.setState({ activeSearchTabIndex: Number(`${newIndex}`) });
        window.sessionStorage.setItem('home-active-search-tab-index', newIndex);
    };

    handleSearchFilterChange = (event) => {
        const value = event.target.value;
        this.setState({
            waysToStay: value,
        });
    };

    onWaysToStayApplyClicked = () => {};

    submitTicketsSearch = () => {
        const { search } = this.props;

        try {
            const suggestion = search.selectedSuggestion || {
                text: search.what,
            };

            const uiFilters = this.applyTicketsUIFilters();

            this.setState({ searchLoading: true });

            suggestion.checkin_time = search.checkin_time;
            suggestion.checkout_time = search.checkout_time;
            suggestion.uiFilters = uiFilters;
            SearchActions.addSearchHistoryItem({
                rawSearchText: search.what,
                suggestion,
            });

            const tourTimeFilter = this.buildTourTimeFilter();

            SearchActions.fetchInventoryAvailabilityResults(
                search.what,
                suggestion?.entity_id,
                suggestion?.entity_type,
                uiFilters,
                search.checkin_time,
                search.checkout_time,
                null,
                true,
                true,
                null,
                null,
                tourTimeFilter
            )
                .then((result) => {
                    let firstResult = null;
                    if (result.data.results && result.data.results.length > 0) {
                        firstResult = result.data.results[0];
                        if (
                            search.what?.toLowerCase().trim() ===
                                firstResult.name.toLowerCase() &&
                            firstResult.entity_type === 'ticketfacility'
                        ) {
                            const url = MapHelper.getTargetUri(firstResult);

                            window.location.assign(url);

                            return;
                        }
                    }
                    this.showMainSearchResults('Tickets');
                })
                .catch(() => {
                    this.showMainSearchResults('Tickets');
                });
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('error', err);

            this.setState({ searchLoading: false });
        }
    };

    submitPermitsSearch = () => {
        const { search } = this.props;

        try {
            const suggestion = search.selectedSuggestion || {
                text: search.what,
            };

            const uiFilters = this.applyPermitsUIFilters();

            this.setState({ searchLoading: true });

            suggestion.uiFilters = uiFilters;
            SearchActions.addSearchHistoryItem({
                rawSearchText: search.what,
                suggestion,
            });

            SearchActions.fetchInventoryAvailabilityResults(
                search.what,
                suggestion?.entity_id,
                suggestion?.entity_type,
                uiFilters,
                search.checkin_time,
                search.checkout_time
            )
                .then((result) => {
                    let firstResult = null;
                    if (result.data.results && result.data.results.length > 0) {
                        firstResult = result.data.results[0];
                        if (
                            search.what?.toLowerCase().trim() ===
                                firstResult.name.toLowerCase() &&
                            firstResult.entity_type === 'permit'
                        ) {
                            const url = MapHelper.getTargetUri(firstResult);

                            window.location.assign(url);

                            return;
                        }
                    }
                    this.showMainSearchResults('Permits');
                })
                .catch(() => {
                    this.showMainSearchResults('Permits');
                });
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('error', err);

            this.setState({ searchLoading: false });
        }
    };

    submitDayUseVenuesSearch = () => {
        const { search } = this.props;

        try {
            const suggestion = search.selectedSuggestion || {
                text: search.what,
            };

            const uiFilters = this.applyDayUseVenuesUiFilters();

            this.setState({ searchLoading: true });

            suggestion.checkin_time = search.checkin_time;
            suggestion.checkout_time = search.checkout_time;
            suggestion.uiFilters = uiFilters;
            SearchActions.addSearchHistoryItem({
                rawSearchText: search.what,
                suggestion,
            });

            SearchActions.fetchInventoryAvailabilityResults(
                search.what,
                suggestion?.entity_id,
                suggestion?.entity_type,
                uiFilters,
                search.checkin_time,
                search.checkout_time
            )
                .then((result) => {
                    let firstResult = null;

                    if (result.data.results && result.data.results.length > 0) {
                        firstResult = result.data.results[0];
                        if (
                            search.what?.toLowerCase().trim() ===
                                firstResult.name.toLowerCase() &&
                            (firstResult.campsite_type_of_use.includes('Day') ||
                                firstResult.campsite_type_of_use.includes('Overnight'))
                        ) {
                            const url = MapHelper.getTargetUri(firstResult);

                            window.location.assign(url);

                            return;
                        }
                    }
                    this.showMainSearchResults('Day Use / Venues');
                })
                .catch(() => {
                    this.showMainSearchResults('Day Use / Venues');
                });
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('error', err);

            this.setState({ searchLoading: false });
        }
    };

    shouldRenderAccessibleCamping = () => {
        const { activeSearchTabIndex } = this.state;

        if (activeSearchTabIndex === 0 || activeSearchTabIndex === 1) {
            return <AccessibleCheckbox iconSize="sm" />;
        }

        return null;
    };

    getClickTagActionComplement = () => {
        const { search } = this.props;

        if (search.what) {
            return ` - ${search.what}`;
        }

        return '';
    };

    buildGATrackingObject = (suggestion, tab, isNearMe = false) => {
        let clickTagAction = '';
        const clickTagCategory = 'Homepage';
        const clickTagLabel = 'Hero';
        if (suggestion) {
            clickTagAction = `${tab}${
                suggestion.last_used ? ' History' : ''
            } Suggestion - ${suggestion.name}`;
        }

        if (isNearMe) {
            clickTagAction = `${tab} - Select Search Suggestion Near Me`;
        }

        return {
            clickTagCategory,
            clickTagAction,
            clickTagLabel,
        };
    };

    onFlexFiltersChange = (flexDatesRaw) => {
        const params = { ...flexDatesRaw };

        if (Object.keys(params).length === 0) {
            this.setState({ flexDatesFilter: {} });
            return;
        }

        if (params?.duration && params?.months) {
            params.startDate = null;
            params.endDate = null;
        } else {
            params.duration = null;
            params.months = null;
        }
        this.setState({
            flexDatesFilter: params,
        });
    };

    getCampingWaysToStayList = () => {
        return [
            {
                id: `homepage-search-options-all`,
                className: 'mb-2',
                label: 'All',
                value: 'All',
            },
            {
                id: `homepage-search-options-rv-camping`,
                className: 'mb-2',
                label: 'RV/Motorhome/Trailer Camping',
                value: 'RV/Motorhome/Trailer Camping',
            },
            {
                id: `homepage-search-options-tent-camping`,
                className: 'mb-2',
                label: 'Tent Camping',
                value: 'Tent Camping',
            },
            {
                id: `homepage-search-options-cabin-yurts`,
                label: 'Lodging (Cabins/Yurts/Lookouts)',
                value: 'Lodging (Cabins/Yurts/Lookouts)',
            },
        ];
    };

    render() {
        const {
            flexDatesFilter,
            startDate,
            useMobile,
            waysToStay,
            windowWidth,
            searchLoading,
            photoFlyout,
            activeSearchTabIndex,
            searchPlaceholder: stateSearchPlaceholder,
            searchPlaceholderCamping: stateSearchPlaceholderCamping,
        } = this.state;
        const { submitSimpleSearch } = this.props;

        // Hero Image Info (used by the photo-credit flyout below)
        const locationName = this.randomHeroImage.locationName;
        const locationLink = this.randomHeroImage.locationLink;
        const photoCredit = this.randomHeroImage.photoCredit;

        const heroImage = <HeroImage image={this.randomHeroImage} />;

        let searchPlaceholder = stateSearchPlaceholder;
        const searchPlaceholderCamping = stateSearchPlaceholderCamping;

        if (useMobile) {
            searchPlaceholder = 'Search for camping, tours and more...';
        }

        const defaultDropdownText = 'Ways to Stay';
        const waysToStayFilterLabel =
            waysToStay === 'All' ? (
                <>
                    <Icon iconName="tent" />
                    {defaultDropdownText}
                </>
            ) : (
                <>
                    <Icon iconName="tent" />
                    {waysToStay}
                </>
            );

        if (windowWidth < 768) {
            return (
                <MobileSearch
                    isLoading={searchLoading}
                    submitSearch={this.submitSearch}
                    runSearch={this.runSearch}
                    heroImage={heroImage}
                />
            );
        }

        const allowedInventoryTypes = process.env.ALLOWED_INVENTORY_TYPES;
        const waysToStayList = this.getCampingWaysToStayList();

        return (
            <section
                className={cx('nav-hero-wrap', {
                    'ai-search-active':
                        activeSearchTabIndex === AI_SEARCH_TAB_INDEX &&
                        !!this.props.flags?.useIaSearchParse,
                })}
            >
                <div className="nav-hero-content-wrap">
                    <PhotoInfoFlyout
                        isOpen={photoFlyout}
                        open={this.openPhotoCredit}
                        close={this.closePhotoCredit}
                    >
                        <p>
                            <strong>Photo Location:</strong>{' '}
                            <Button
                                appearance="link"
                                isUnstyled
                                href={`${locationLink}`}
                                aria-label={`Visit ${locationName}`}
                                gaTrackingId="644652766927"
                                gaTrackingValue={`Image Location Link to - ${locationName}`}
                            >
                                {locationName}
                            </Button>
                            {photoCredit && (
                                <>
                                    {' '}
                                    | <strong>Photo Credit:</strong> {photoCredit}
                                </>
                            )}
                        </p>
                    </PhotoInfoFlyout>

                    <FlexRow hasGutters={false}>
                        <FlexCol>
                            <div
                                className={cx('nav-hero-search-section-wrap', {
                                    'photo-fly-out': photoFlyout,
                                })}
                            >
                                {photoFlyout && (
                                    <div
                                        className="photo-fly-out-layer"
                                        onClick={this.closePhotoCredit}
                                        aria-hidden="true"
                                    />
                                )}
                                <Tabs
                                    stretchTabsToFit={false}
                                    activeIndex={activeSearchTabIndex}
                                    onChange={this.handleSearchTabChange}
                                    tabsAccessoryComponent={this.shouldRenderAccessibleCamping()}
                                >
                                    <TabPanel
                                        label="Explore All"
                                        gaTrackingId="103481690315"
                                        gaTrackingValue="Explore All - search tab"
                                    >
                                        <div
                                            role="search"
                                            aria-label="Explore All"
                                            className="nav-search-input-wrapper"
                                        >
                                            <div className="form-item-wrap">
                                                <SearchAutosuggest
                                                    buildGATrackingObject={(
                                                        suggestion,
                                                        isNearMe
                                                    ) =>
                                                        this.buildGATrackingObject(
                                                            suggestion,
                                                            'Explore All',
                                                            isNearMe
                                                        )
                                                    }
                                                    id="hero-search-input"
                                                    key="hero-search-input"
                                                    size="lg"
                                                    placeholder={searchPlaceholder}
                                                    contentTypes={['inventory']}
                                                    onRunSearch={this.runSearch}
                                                    defaultEventCategory="Homepage"
                                                    shouldPopoverFlip={false}
                                                    updateOnScroll={true}
                                                />
                                            </div>

                                            <Button
                                                className="nav-search-button"
                                                isLoading={searchLoading}
                                                onClick={this.submitSearch}
                                                gaTrackingId="141992425485"
                                                gaTrackingValue={`Explore All Search Initiated${this.getClickTagActionComplement()}`}
                                            >
                                                SEARCH
                                            </Button>
                                        </div>

                                        <div>
                                            <Divider />

                                            <ExploreAllQuickPromos
                                                search={submitSimpleSearch}
                                            />
                                        </div>
                                    </TabPanel>

                                    <TabPanel
                                        label="Camping & Lodging"
                                        isHidden={!allowedInventoryTypes?.camping}
                                        gaTrackingId="103481690315"
                                        gaTrackingValue="Camping & Lodging - search tab"
                                    >
                                        <div
                                            role="search"
                                            aria-label="Camping & Lodging"
                                            className="nav-search-input-wrapper"
                                        >
                                            <div className="form-item-wrap">
                                                <SearchAutosuggest
                                                    size="lg"
                                                    buildGATrackingObject={(
                                                        suggestion,
                                                        isNearMe
                                                    ) =>
                                                        this.buildGATrackingObject(
                                                            suggestion,
                                                            'Camping & Lodging',
                                                            isNearMe
                                                        )
                                                    }
                                                    placeholder={searchPlaceholderCamping}
                                                    onRunSearch={this.submitCampingSearch}
                                                    id="hero-camping-search-input"
                                                    key="hero-camping-search-input"
                                                    inventoryTypes={
                                                        SEARCH_INVENTORY_TYPES.CAMPING_TAB
                                                    }
                                                    contentTypes={['inventory']}
                                                    searchOnSelectTypes={
                                                        ENABLED_SEARCH_ON_SELECT_TYPES
                                                    }
                                                    disableSearchOnSelect
                                                    defaultEventCategory="Homepage"
                                                    geocoder
                                                    shouldPopoverFlip={false}
                                                />
                                            </div>

                                            <DropdownMenu
                                                id="homepage-search-options"
                                                label={waysToStayFilterLabel}
                                                onApply={this.onWaysToStayApplyClicked}
                                                className={
                                                    waysToStayFilterLabel ===
                                                    defaultDropdownText
                                                        ? ''
                                                        : 'homepage-active-dropdown-text'
                                                }
                                            >
                                                <div style={{ minWidth: '200px' }}>
                                                    <RadioGroup
                                                        name="homepage-search-camping-options"
                                                        value={waysToStay}
                                                        onChange={
                                                            this.handleSearchFilterChange
                                                        }
                                                    >
                                                        {waysToStayList.map((item) => (
                                                            <Radio
                                                                key={item.id}
                                                                id={item.id}
                                                                className={item.className}
                                                                label={item.label}
                                                                value={item.value}
                                                            />
                                                        ))}
                                                    </RadioGroup>
                                                </div>
                                            </DropdownMenu>

                                            <div className="nav-search-flex-dates">
                                                <HeroDates
                                                    onLegacyFiltersChange={
                                                        this.onFlexFiltersChange
                                                    }
                                                    legacyFlexFilters={flexDatesFilter}
                                                />
                                            </div>

                                            <Button
                                                className="nav-search-button"
                                                onClick={this.submitCampingSearch}
                                                isLoading={searchLoading}
                                                isDisabled={searchLoading}
                                                gaTrackingId="955634121558"
                                                gaTrackingValue={`Camping & Lodging Search Initiated${this.getClickTagActionComplement()}`}
                                            >
                                                SEARCH
                                            </Button>
                                        </div>

                                        <Divider />

                                        <CampingCarousel />

                                        {/* <CampingLodgingCarousel
                                                itemWidth={navSearchCarouselItemsWidth}
                                                data={campingTabCarouselData}
                                                enableAvailableThisWeekend={
                                                    enableAvailableThisWeekend
                                                }
                                                dates={{
                                                    startDate: campingStartDate,
                                                    endDate: campingEndDate,
                                                }}
                                            /> */}
                                    </TabPanel>

                                    <TabPanel
                                        label="Tickets & Tours"
                                        isHidden={!allowedInventoryTypes?.tickets}
                                        gaTrackingId="103481690315"
                                        gaTrackingValue="Tickets & Tours - search tab"
                                    >
                                        <div
                                            role="search"
                                            aria-label="Tickets & Tours"
                                            className="nav-search-input-wrapper"
                                        >
                                            <div className="form-item-wrap">
                                                <SearchAutosuggest
                                                    size="lg"
                                                    buildGATrackingObject={(
                                                        suggestion,
                                                        isNearMe
                                                    ) =>
                                                        this.buildGATrackingObject(
                                                            suggestion,
                                                            'Tickets & Tours',
                                                            isNearMe
                                                        )
                                                    }
                                                    placeholder="Location or facility name ..."
                                                    onRunSearch={this.submitTicketsSearch}
                                                    id="hero-tickets-search-input"
                                                    key="hero-tickets-search-input"
                                                    inventoryTypes={
                                                        SEARCH_INVENTORY_TYPES.TICKETS_TOURS_TAB
                                                    }
                                                    contentTypes={['inventory']}
                                                    searchOnSelectTypes={
                                                        ENABLED_SEARCH_ON_SELECT_TYPES
                                                    }
                                                    defaultEventCategory="Homepage"
                                                    disableSearchOnSelect
                                                    shouldPopoverFlip={false}
                                                />
                                            </div>

                                            <TourTimeFilter
                                                onTourTimeChange={this.onTourTimeChange}
                                            />

                                            <HeroSingleDate
                                                label="Tickets & Tours date picker"
                                                value={startDate}
                                                onChange={this.onSingleDateChange}
                                            />

                                            <Button
                                                className="nav-search-button"
                                                isLoading={searchLoading}
                                                isDisabled={searchLoading}
                                                onClick={this.submitTicketsSearch}
                                                gaTrackingId="393900084528"
                                                gaTrackingValue={`Tickets & Tours Search Initiated${this.getClickTagActionComplement()}`}
                                            >
                                                SEARCH
                                            </Button>
                                        </div>

                                        <Divider />

                                        <TicketsCarousel />
                                    </TabPanel>

                                    <TabPanel
                                        label="Permits"
                                        isHidden={!allowedInventoryTypes?.permits}
                                        gaTrackingId="103481690315"
                                        gaTrackingValue="Permits - search tab"
                                    >
                                        <div
                                            role="search"
                                            aria-label="Permits"
                                            className="nav-search-input-wrapper"
                                        >
                                            <div className="form-item-wrap">
                                                <SearchAutosuggest
                                                    size="lg"
                                                    buildGATrackingObject={(
                                                        suggestion,
                                                        isNearMe
                                                    ) =>
                                                        this.buildGATrackingObject(
                                                            suggestion,
                                                            'Permits',
                                                            isNearMe
                                                        )
                                                    }
                                                    placeholder="Location or facility name ..."
                                                    onRunSearch={this.submitPermitsSearch}
                                                    id="hero-permits-input"
                                                    key="hero-permits-input"
                                                    inventoryTypes={
                                                        SEARCH_INVENTORY_TYPES.PERMITS_TAB
                                                    }
                                                    contentTypes={['inventory']}
                                                    defaultEventCategory="Homepage"
                                                    shouldPopoverFlip={false}
                                                />
                                            </div>

                                            <Button
                                                className="nav-search-button"
                                                isLoading={searchLoading}
                                                isDisabled={searchLoading}
                                                onClick={this.submitPermitsSearch}
                                                gaTrackingId="362892068574"
                                                gaTrackingValue={`Permits Search Initiated${this.getClickTagActionComplement()}`}
                                            >
                                                SEARCH
                                            </Button>
                                        </div>

                                        <Divider />

                                        <PermitsCarouselNew />
                                    </TabPanel>

                                    <TabPanel
                                        label="Day Use / Venues"
                                        isHidden={!allowedInventoryTypes?.venues}
                                        gaTrackingId="103481690315"
                                        gaTrackingValue="Day Use / Venues - search tab"
                                    >
                                        <div
                                            role="search"
                                            aria-label="Day Use / Venues"
                                            className="nav-search-input-wrapper"
                                        >
                                            <div className="form-item-wrap">
                                                <SearchAutosuggest
                                                    size="lg"
                                                    buildGATrackingObject={(
                                                        suggestion,
                                                        isNearMe
                                                    ) =>
                                                        this.buildGATrackingObject(
                                                            suggestion,
                                                            'Day Use / Venues',
                                                            isNearMe
                                                        )
                                                    }
                                                    placeholder="Location or facility name ..."
                                                    onRunSearch={
                                                        this.submitDayUseVenuesSearch
                                                    }
                                                    id="hero-dayuse-venues-input"
                                                    key="hero-dayuse-venues-input"
                                                    inventoryTypes={
                                                        SEARCH_INVENTORY_TYPES.DAY_USE_VENUES_TAB
                                                    }
                                                    contentTypes={['inventory']}
                                                    searchOnSelectTypes={
                                                        ENABLED_SEARCH_ON_SELECT_TYPES
                                                    }
                                                    defaultEventCategory="Homepage"
                                                    disableSearchOnSelect
                                                    shouldPopoverFlip={false}
                                                />
                                            </div>

                                            <HeroSingleDate
                                                label="Day Use / Venues date picker"
                                                value={startDate}
                                                onChange={this.onSingleDateChange}
                                            />

                                            <Button
                                                className="nav-search-button"
                                                isLoading={searchLoading}
                                                isDisabled={searchLoading}
                                                onClick={this.submitDayUseVenuesSearch}
                                                gaTrackingId="539995011624"
                                                gaTrackingValue={`Day Use / Venues Search Initiated${this.getClickTagActionComplement()}`}
                                            >
                                                SEARCH
                                            </Button>
                                        </div>

                                        <Divider />

                                        <VenuesCarousel />
                                    </TabPanel>
                                    <TabPanel
                                        label={
                                            <FlexRow>
                                                <FlexCol>
                                                    AI Search{' '}
                                                    <Badge
                                                        className="ml-half"
                                                        appearance="info"
                                                    >
                                                        Beta
                                                    </Badge>
                                                </FlexCol>
                                            </FlexRow>
                                        }
                                        isHidden={!this.props.flags?.useIaSearchParse}
                                        gaTrackingId="103481690315"
                                        gaTrackingValue="Ai Search - search tab"
                                    >
                                        <HeroAiSearch />
                                    </TabPanel>
                                </Tabs>
                            </div>
                        </FlexCol>
                    </FlexRow>
                </div>

                {heroImage}
            </section>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        search: state.search,
    };
};

export default withLDConsumer()(connect(mapStateToProps)(HeroContainer));

// cSpell:ignore ticketfacility, dayuse, checkin, geoip, venuereservations, Criterias, timedentry, destin, frasier
